<template>
  <div class="area-inner">
    <Transition name="el-fade-in-linear">
      <div
        v-if="!draftItemLoading && !nextActionLoading && !reserveItemLoading"
        class="task-area"
      >
        <DraftItemArea
          v-if="!draftItemLoading && draftItems.length > 0"
          class="mb-300"
          :draft-items="draftItems"
          @click:direct-mail-confirm="handleClickConfirmDirectMail"
          @click:sequence-action="handleClickSequenceAction"
          @click:next-action-suggestion="handleClickNextActionSuggestion"
          @after-delete-direct-mail="handleAfterDeleteDirectMail"
        />
        <NextActionArea
          v-if="!nextActionLoading && existNextAction"
          class="mb-300"
          v-bind="nextActionProps"
          @update="updateNextAction"
          v-on="nextActionFunctions"
          @click:direct-mail-confirm="handleClickConfirmDirectMail"
          @click:sequence-action="handleClickSequenceAction"
          @after-delete-next-action="handleAfterDeleteNextAction"
        />
        <ReserveItemArea
          v-if="!reserveItemLoading && reserveItems.length > 0"
          :reserve-items="reserveItems"
          :key-of-get-reserve-items-wait="keyOfGetReserveItemsWait"
          @click:direct-mail-confirm="handleClickConfirmDirectMail"
          @click:sequence-action="handleClickSequenceAction"
          @after-delete-direct-mail="handleAfterDeleteDirectMail"
        />
      </div>
    </Transition>
    <div class="history-area">
      <LeadHistoriesFilter
        v-model:history-type="historyType"
        :call-target="callTarget"
      />
      <BPlaceholder
        v-if="historyLoading"
        v-loading="true"
        height="50vh"
        element-loading-background="transparent"
      />
      <template v-else>
        <TransitionGroup
          v-show="!historyLoading && existHistory"
          name="list"
          tag="div"
        >
          <div
            v-if="leadHistories.length > 0"
            v-infinite-scroll="load"
            class="column layout"
            infinite-scroll-disabled="scrollDisabled"
            infinite-scroll-distance="20"
          >
            <div
              v-for="historyItem in filterLeadHistories"
              :key="`${historyItem.type}-${historyItem.id}`"
              ref="historyItems"
              :data-uuid="`${historyItem.uuid}`"
            >
              <LeadHistoryCard
                class="mb-50"
                :history-item="historyItem"
                :call-target="callTarget"
                :call-target-id="callTargetId"
                :is-zoom-phone-connected="isZoomPhoneConnected"
                :functions="{
                  callResult: callResultFunctions,
                  nextAction: nextActionFunctions,
                  opportunity: opportunityFunctions,
                  freeAction: freeActionFunctions,
                  mail: {
                    replyToReceivedMail: handleReplyToReceivedMail,
                    replyToSentDirectMail: handleReplyToSentDirectMail,
                  },
                }"
              />
            </div>
          </div>
          <BLayout
            v-if="scrollDisabled && $wait.is('infiniteLoadingWait')"
            class="py-200"
            justify-center
          >
            <BElementIcon
              v-loading="true"
              name="loading"
            />
          </BLayout>
        </TransitionGroup>
        <div v-if="!pageLoading && !existHistory">
          <BEmptyBox
            class="pt-600 pb-600"
            display-only
          >
            <div v-if="historyType">
              {{ $t('apiError.notFound') }}
            </div>
            <div v-else>
              {{ $t('callHistory.emptyData') }}
            </div>
          </BEmptyBox>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapWaitingActions } from 'vue-wait';
import { mapGetters } from 'vuex';
import LeadHistoryCard from '@/components/organisms/user/general/LeadHistoryCard.vue';
import DraftItemArea from '@/components/organisms/user/lead/DraftItemArea.vue';
import LeadHistoriesFilter from '@/components/organisms/user/lead/history/LeadHistoriesFilter.vue';
import NextActionArea from '@/components/organisms/user/lead/NextActionArea.vue';
import ReserveItemArea from '@/components/organisms/user/lead/ReserveItemArea.vue';
import { useAlert } from '@/composable/message-dialog';
import { useScrollToHistoryItem } from '@/composable/user/use-scroll-to-history-item';
import errorHandler from '@/mixins/error_handler';
import { getIsZoomPhoneConnected } from '@/services/cti_service';

export default {
  components: {
    LeadHistoriesFilter,
    DraftItemArea,
    LeadHistoryCard,
    NextActionArea,
    ReserveItemArea,
  },
  mixins: [errorHandler],
  props: {
    callTarget: Object,
    callTargetId: Number,
    isEditing: Boolean,
    pageLoading: Boolean,
    draftItems: Array,
    reserveItems: Array,
    keyOfGetReserveItemsWait: String,
  },
  emits: [
    'selectDirectMail',
    'replyToReceivedMail',
    'replyToSentDirectMail',
    'afterDeleteDirectMail',
    'deleteLeadHistory',
    'fetchNextActionItems',
    'fetchSequenceItems',
    'clearSelectedSequenceInstance',
    'selectSequenceInstance',
    'click:callResultEdit',
    'selectNextActionSuggestion',
  ],
  setup() {
    const { alert } = useAlert();
    const { scrollToHistoryItem } = useScrollToHistoryItem();
    return {
      alert,
      scrollToHistoryItem,
    };
  },
  data() {
    return {
      timeObj: null,
      saveSfOpportunityErrors: [],
      displayCount: 20,
      increaseNumber: 20,
      scrollDisabled: false,
      isZoomPhoneConnected: false,
      historyType: undefined,
    };
  },
  computed: {
    ...mapGetters('user', [
      'nextActions',
      'leadHistories',
      'currentUser',
    ]),
    existNextAction () {
      return this.nextActions.length > 0;
    },
    existHistory () {
      return this.leadHistories.length > 0;
    },
    draftItemLoading () {
      return this.$wait.is('getDraftItemsWait');
    },
    nextActionLoading () {
      return this.$wait.is('getNextActionsWait');
    },
    reserveItemLoading () {
      return this.$wait.is(this.keyOfGetReserveItemsWait);
    },
    historyLoading () {
      return this.$wait.is('getLeadHistoriesExplicitWait') || this.historyActionLoading;
    },
    historyActionLoading () {
      return this.$wait.is([
        'getLeadHistoriesWait',
        'getLeadHistoriesBackgroundWait',
        'deleteCallResultWait',
        'deleteOpportunityWait',
        'deleteFreeActionWait',
      ]);
    },
    nextActionProps () {
      return {
        nextActions: this.nextActions,
        callTarget: this.callTarget,
      };
    },
    nextActionFunctions () {
      return {
        'done-cancel': this.handleDoneCancelNextAction,
        'click-edit': this.editNextAction,
        'click-delete': this.deleteNextAction,
        'click-done': this.handleDoneNextAction,
        'click-drawer-close': this.handleNextActionDrawerClose,
      };
    },
    callResultFunctions () {
      return {
        'click-edit': this.editCallResult,
        'click-delete': this.deleteCallResult,
      };
    },
    opportunityFunctions () {
      return {
        'click-edit': this.editOpportunity,
        'click-delete': this.deleteOpportunity,
      };
    },
    freeActionFunctions () {
      return {
        'click-delete': this.deleteFreeAction,
      };
    },
    filterLeadHistories() {
      return this.leadHistories.slice(0, this.displayCount);
    },
  },
  watch: {
    leadHistories() {
      if (this.$route.query.historyItem) {
        this.displayCount = this.leadHistories.length;
        this.scrollDisabled = false;
      } else {
        this.displayCount = 20;
        this.scrollDisabled = false;
      }
    },
    historyType() {
      this.getLeadHistoriesExplicitAction({ callTargetId: this.callTargetId, params: { historyType: this.historyType } });
    },
    historyActionLoading() {
      this.historyType = undefined;
    },
  },
  created() {
    this.timeObj = this.$setInterval(() => {
      this.getLeadHistoriesImplicitAction({ callTargetId: this.callTargetId, params: { historyType: this.historyType } });
    }, 90000);
    this.scrollToHistoryItemIfNeeded();
    this.fetchZoomPhoneConnected();
  },
  beforeUnmount() {
    this.$clearInterval(this.timeObj);
  },
  unmounted() {
    this.displayCount = 20;
    this.scrollDisabled = false;
  },
  methods: {
    ...mapWaitingActions('user', {
      getCallTargetsBackgroundAction: { action: 'getCallTargetsAction', loader: 'getCallTargetsBackgroundWait' },
      getLeadHistoriesExplicitAction: { action: 'getLeadHistoriesAction', loader: 'getLeadHistoriesExplicitWait' },
      getLeadHistoriesImplicitAction: { action: 'getLeadHistoriesAction', loader: 'getLeadHistoriesImplicitWait' },
      getNextActionsAction: 'getNextActionsWait',
      updateNextActionAction: 'updateNextActionWait',
      doneNextAction: 'doneNextActionWait',
      doneCancelNextAction: 'doneCancelNextActionWait',
      deleteCallResultAction: 'deleteCallResultWait',
      deleteOpportunityAction: 'deleteOpportunityWait',
      deleteFreeActionAction: 'deleteFreeActionWait',
      deleteNextActionAction: 'deleteNextActionWait',
    }),
    ...mapWaitingActions('userUi', {
      setSelectedOpportunityAction: 'setSelectedOpportunityWait',
      setSelectedNextActionAction: 'setSelectedNextActionWait',
      setTargetOpportunityFormVisibleAction: 'setTargetOpportunityFormVisibleWait',
      setTargetSingleNextActionFormVisibleAction: 'setTargetSingleNextActionFormVisibleWait',
    }),
    async fetchZoomPhoneConnected() {
      this.isZoomPhoneConnected = await getIsZoomPhoneConnected();
    },
    async updateNextAction (nextAction) {
      await this.updateNextActionAction({
        callTargetId: this.callTargetId,
        nextActionId: nextAction.id,
        body: { nextAction },
        errorHandlers: {
          422: this.defaultHandler,
        },
      });
      this.$emit('fetchNextActionItems');
    },
    async handleDoneNextAction (nextAction) {
      await this.doneNextAction({
        callTargetId: this.callTargetId,
        nextActionId: nextAction.id,
        errorHandlers: {
          422: this.defaultHandler,
        },
      });

      if (nextAction.sequenceStepInstance) {
        this.$emit('clearSelectedSequenceInstance');
        this.$emit('fetchSequenceItems');
      } else {
        this.$emit('fetchNextActionItems');
      }
    },
    async handleDoneCancelNextAction (nextAction) {
      await this.doneCancelNextAction({
        callTargetId: this.callTargetId,
        nextActionId: nextAction.id,
        errorHandlers: {
          422: this.defaultHandler,
        },
      });
      this.$emit('fetchNextActionItems');
    },
    editCallResult (callResult) {
      this.$emit('click:callResultEdit', callResult);
    },
    editOpportunity (opportunity) {
      this.setSelectedOpportunityAction(opportunity);
      this.setTargetOpportunityFormVisibleAction(true);
    },
    handleClickConfirmDirectMail(directMail) {
      this.$emit('selectDirectMail', directMail);
    },
    handleAfterDeleteDirectMail() {
      this.$emit('afterDeleteDirectMail');
    },
    handleReplyToReceivedMail(receivedMailId) {
      this.$emit('replyToReceivedMail', receivedMailId);
    },
    handleReplyToSentDirectMail(directMailId) {
      this.$emit('replyToSentDirectMail', directMailId);
    },
    handleAfterDeleteNextAction() {
      this.$emit('fetchNextActionItems');
    },
    editNextAction (nextAction) {
      this.setSelectedNextActionAction(nextAction);
      this.setTargetSingleNextActionFormVisibleAction(true);
    },
    handleNextActionDrawerClose () {
      this.setSelectedNextActionAction({});
      this.setTargetSingleNextActionFormVisibleAction(false);
    },
    handleClickSequenceAction(sequenceInstanceId) {
      this.$emit('selectSequenceInstance', sequenceInstanceId);
    },
    handleClickNextActionSuggestion(nextActionSuggestion) {
      this.$emit('selectNextActionSuggestion', nextActionSuggestion);
    },
    async deleteCallResult (callResultId) {
      const check = await this.$bitterAlert.show({
        title: this.$t('callHistory.title'),
        text: this.$t('general.confirm.of', { action: this.$t('general.delete.text') }),
      });
      if (!check) { return; }
      await this.deleteCallResultAction({
        callResultId,
        callTargetId: this.callTargetId,
      });

      await this.getNextActionsAction({ callTargetId: this.callTargetId });
      this.$emit('deleteLeadHistory');
    },
    async deleteOpportunity (opportunityId) {
      const check = await this.$bitterAlert.show({
        title: this.$t('callHistory.title'),
        text: this.$t('general.confirm.of', { action: this.$t('general.delete.text') }),
      });
      if (!check) { return; }
      await this.deleteOpportunityAction({
        opportunityId,
        callTargetId: this.callTargetId,
      });

      this.$emit('deleteLeadHistory');
    },
    async deleteFreeAction (freeActionId) {
      const check = await this.$bitterAlert.show({
        title: this.$t('callHistory.title'),
        text: this.$t('general.confirm.of', { action: this.$t('general.delete.text') }),
      });
      if (!check) { return; }
      await this.deleteFreeActionAction({
        freeActionId,
        callTargetId: this.callTargetId,
      });

      this.$emit('deleteLeadHistory');
    },
    async deleteNextAction (nextActionId) {
      const check = await this.$bitterAlert.show({
        title: this.$t('nextAction.title'),
        text: this.$t('general.confirm.of', { action: this.$t('general.delete.text') }),
      });
      if (!check) { return; }
      await this.deleteNextActionAction({
        nextActionId,
        callTargetId: this.callTargetId,
      });
      this.$emit('fetchNextActionItems');
    },
    load() {
      if (this.leadHistories.length <= this.displayCount) return;
      this.$wait.start('infiniteLoadingWait');
      this.scrollDisabled = true;
      setTimeout(() => {
        this.displayCount += this.increaseNumber;
        this.$wait.end('infiniteLoadingWait');
        this.scrollDisabled = false;
      }, 500);
    },
    scrollToHistoryItemIfNeeded() {
      // NOTE: vuer-routerを使わずクエリパラメータを操作している箇所があるため、windowから直接取る。
      const urlParams = new URLSearchParams(window.location.search);
      const uuid = urlParams.get('historyItem');
      if (uuid == null) return;

      // 簡素な関数で値を取得
      this.scrollToHistoryItem(
        uuid,
        () => this.$refs.historyItems,
        () => this.historyLoading,
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.area-inner {
  min-width: 820px;
  width: 100%;
  min-width: 600px;
  margin: 0 auto;
}
.history-area {
  padding: $basespace-300 $basespace-600 $basespace-600;
}

$tasks: 'next-action', 'reserve-item', 'draft-item';
@each $task in $tasks {
  .#{$task}-area {
    background-color: $bgcolor-white;
    padding: $basespace-100 0;

    :deep(.#{$task}-task) {
      transition: all 300ms $standard-easing;

      &:hover {
        background: $bgcolor-base;
        cursor: pointer;
      }
    }
  }
}
</style>
